import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Container, Graphics, Text, Sprite } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeBetSelectPopup,
  setBet,
  closeMenu,
} from "../../../../State/actions";
import {
  selectBetSelectPopupActive,
  selectBets,
  selectBet,
  selectBalance,
  selectCurrency,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { getNewUITexture } from "../../../../assets";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import BetSelectContent from "./BetSelectContent";
import anime from "animejs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";

// ── Overlay constants ─────────────────────────────────────────────────────
const OVERLAY_ALPHA = 1.0;
const OVERLAY_FADE_MS = 200;

// Configuration for popup (fullscreen mode)
const POPUP_CONFIG = {
  // Base values in pixels (will be scaled by uiSymbolScale)
  basePadding: {
    horizontal: 28,
    vertical: 40,
  },
  baseTitleFontSize: 42,
  baseTitleGap: 26,
  baseScrollbarOffset: 0.5,

  // Multipliers for each device/orientation combination
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 2.0, v: 6.0 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.0,
      scrollbarOffsetMultiplier: 1.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 6.5, v: 2.4 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 1.5,
      scrollbarOffsetMultiplier: 1.0,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 2.0, v: 11.0 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.0,
      scrollbarOffsetMultiplier: 1.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 8.0, v: 2.4 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 1.5,
      scrollbarOffsetMultiplier: 1.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.25,
      scrollbarOffsetMultiplier: 2.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 }, // Match autoplay popup
      titleFontMultiplier: 0.9, // Match autoplay popup
      titleGapMultiplier: 0.75, // Match autoplay popup
      scrollbarOffsetMultiplier: 0,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetLeft: 0.95, // Offset from left edge (left side of toolbar)
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 }, // Match AutoplayPopup
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 0.5, // Small gap between title and bet buttons
      scrollbarOffsetMultiplier: 1.0,
    },
  },
};

const BetSelectPopup = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBetSelectPopupActive);
  const bets = useSelector(selectBets);
  const currentBet = useSelector(selectBet);
  const balance = useSelector(selectBalance);
  const currency = useSelector(selectCurrency);

  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    getResponsiveConfig,
    isMobileSmall,
  } = useDimensions();

  const containerRef = useRef(null);

  // Animation state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedBet, setSelectedBet] = useState(currentBet);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  const handleBetSelect = useCallback(
    (betValue) => {
      soundManager.play(SOUND_NAME.button_click);
      setSelectedBet(betValue);
      dispatch(setBet(betValue));
      handleClose();
    },
    [dispatch, handleClose],
  );

  // Note: Desktop now shows windowed popup when clicking BetDisplay

  // Reset selected bet when popup opens and close menu if open
  useEffect(() => {
    if (popupActive) {
      setSelectedBet(currentBet);
      dispatch(closeMenu());
    }
  }, [popupActive, currentBet, dispatch]);

  // Windowed mode for tablet landscape and desktop - memoized
  const showPopupFrame = useMemo(
    () =>
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE) ||
      device === PixiSettings.DEVICE.DESKTOP,
    [device, orientation],
  );

  // Get button background texture for natural aspect ratio - memoized
  const buttonBackgroundTexture = useMemo(
    () => getNewUITexture("bet_buttons_background"),
    [],
  );
  const buttonAspectRatio = useMemo(
    () =>
      buttonBackgroundTexture
        ? buttonBackgroundTexture.width / buttonBackgroundTexture.height
        : 2.0, // Fallback to 2:1
    [buttonBackgroundTexture],
  );

  // Get popup frame texture - use horizontal frame for tablet landscape - memoized
  const popupFrameTexture = useMemo(
    () => getNewUITexture(showPopupFrame ? "popup_totalbet" : "popup_totalbet"),
    [showPopupFrame],
  );

  // Popup size and scale calculation
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) return { width: 0, height: 0, scale: 1 };

    if (showPopupFrame) {
      // Windowed mode: Use native texture size scaled by uiSymbolScale and multiplier
      // Adjust multipliers to change popup size per device
      const sizeMultiplier = device === PixiSettings.DEVICE.DESKTOP ? 0.8 : 1.0;

      const finalScale = uiSymbolScale * sizeMultiplier;
      return {
        width: popupFrameTexture.width * finalScale,
        height: popupFrameTexture.height * finalScale,
        scale: finalScale,
      };
    }

    // Fullscreen mode: cap width to toolbar width
    const maxWidth = toolbar?.width
      ? Math.min(dimensions.width, toolbar.width)
      : dimensions.width;
    return {
      width: maxWidth,
      height: dimensions.height,
      scale: uiSymbolScale,
    };
  }, [
    popupFrameTexture,
    showPopupFrame,
    dimensions,
    uiSymbolScale,
    device,
    toolbar,
  ]);

  const popupScale = popupSize.scale;

  // Viewport configuration
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) return null;

    if (showPopupFrame) {
      // Windowed mode (Tablet Landscape): Use config for consistency
      const config = POPUP_CONFIG[device]?.[orientation] || {
        paddingMultiplier: { h: 1.0, v: 1.0 },
        titleFontMultiplier: 1.0,
        titleGapMultiplier: 1.0,
        scrollbarOffsetMultiplier: 1.0,
      };

      const paddingH =
        (POPUP_CONFIG.basePadding.horizontal *
          config.paddingMultiplier.h *
          popupScale) /
        2;
      const paddingV =
        POPUP_CONFIG.basePadding.vertical *
        config.paddingMultiplier.v *
        popupScale;
      const contentWidth = popupSize.width - paddingH * 2;
      const contentHeight = popupSize.height - paddingV * 2;
      // Use same scale factor as AutoplayPopup for consistent title sizing
      const autoplayScaleFactor =
        uiSymbolScale * (device === PixiSettings.DEVICE.DESKTOP ? 0.8 : 1.25);
      const titleFontSize =
        Math.round(
          (POPUP_CONFIG.baseTitleFontSize *
            config.titleFontMultiplier *
            autoplayScaleFactor) /
            2,
        ) *
          2 -
        4;
      const titleGap =
        POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * popupScale;
      const titleYOffset = titleFontSize + titleGap;

      return {
        maxWidth: contentWidth,
        maxHeight: contentHeight,
        x: -popupSize.width / 2 + paddingH,
        y: -popupSize.height / 2 + paddingV,
        titleFontSize,
        titleYOffset,
        viewportHeight: contentHeight - titleYOffset,
        scrollbarOffset:
          POPUP_CONFIG.baseScrollbarOffset *
          config.scrollbarOffsetMultiplier *
          popupScale,
      };
    }

    // Fullscreen mode: Get config for current device/orientation
    const configDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const config = POPUP_CONFIG[configDevice]?.[orientation] || {
      paddingMultiplier: { h: 1.0, v: 1.0 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 1.0,
      scrollbarOffsetMultiplier: 1.0,
    };

    // Calculate padding, font size, and gap with multipliers (all scaled by uiSymbolScale)
    const paddingH =
      POPUP_CONFIG.basePadding.horizontal *
      config.paddingMultiplier.h *
      uiSymbolScale;
    const paddingTopMult =
      config.paddingMultiplier.top ?? config.paddingMultiplier.v ?? 1.0;
    const paddingBottomMult =
      config.paddingMultiplier.bottom ?? config.paddingMultiplier.v ?? 1.0;
    const paddingTop =
      POPUP_CONFIG.basePadding.vertical * paddingTopMult * uiSymbolScale;
    const paddingBottom =
      POPUP_CONFIG.basePadding.vertical * paddingBottomMult * uiSymbolScale;
    const contentWidth = popupSize.width - paddingH * 2;
    const contentHeight = dimensions.height - paddingTop - paddingBottom;
    const titleFontSize =
      Math.round(
        (POPUP_CONFIG.baseTitleFontSize *
          config.titleFontMultiplier *
          uiSymbolScale) /
          2,
      ) * 2;
    const titleGap =
      POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * uiSymbolScale;
    const titleYOffset = titleFontSize + titleGap;

    return {
      maxWidth: contentWidth,
      maxHeight: contentHeight,
      x: -popupSize.width / 2 + paddingH,
      y: -dimensions.height / 2 + paddingTop,
      titleFontSize,
      titleYOffset,
      viewportHeight: contentHeight - titleYOffset,
      scrollbarOffset:
        POPUP_CONFIG.baseScrollbarOffset * config.scrollbarOffsetMultiplier,
    };
  }, [
    popupSize,
    dimensions,
    popupFrameTexture,
    showPopupFrame,
    popupScale,
    uiSymbolScale,
    device,
    orientation,
    isMobileSmall,
  ]);

  // Button configuration
  const betButtonConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        width: 100,
        height: 50,
        fontSize: 18,
        spacing: 10,
        rowSpacing: 10,
        columns: 3,
      };

    // Columns: 5 for windowed mode, 5 for mobile landscape, 4 for tablet portrait, 3 for mobile portrait
    const columns = showPopupFrame
      ? 5
      : device === PixiSettings.DEVICE.MOBILE &&
          orientation === PixiSettings.ORIENTATION.LANDSCAPE
        ? 5
        : device === PixiSettings.DEVICE.TABLET &&
            orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? 4
          : 3;
    const gapRatio = 0.05;

    if (showPopupFrame) {
      // Windowed mode: full available width, no scrollbar reservation
      const availableWidth = viewportConfig.maxWidth;
      const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
      // Use 2.2:1 ratio — slightly taller than 2.4
      const buttonHeight = buttonWidth / 2.2;
      const spacing = buttonWidth * gapRatio;
      const rowSpacing = spacing;

      return {
        width: buttonWidth,
        height: buttonHeight,
        fontSize: Math.round((buttonHeight * 0.5) / 2) * 2,
        spacing,
        rowSpacing,
        columns,
      };
    }

    // Fullscreen mode: full available width, no scrollbar reservation
    const availableWidth = viewportConfig.maxWidth;
    const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
    const buttonHeight = buttonWidth / buttonAspectRatio; // Use natural aspect ratio
    const spacing = buttonWidth * gapRatio;
    const rowSpacing = spacing; // Same gap

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize: Math.round((buttonHeight * 0.5) / 2) * 2,
      spacing,
      rowSpacing,
      columns,
    };
  }, [showPopupFrame, viewportConfig, buttonAspectRatio, device, orientation]);

  const buttonSpacing = betButtonConfig.spacing;
  const rowSpacing = betButtonConfig.rowSpacing;
  const gridColumns = betButtonConfig.columns;

  // Memoize translation text - prevents recalculation on every render
  const titleText = useMemo(() => t("total_bet"), [t]);

  // Title text style
  const titleStyle = useMemo(() => {
    if (!viewportConfig) return null;
    return new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: viewportConfig.titleFontSize,
      fontWeight: "600",
      fill: 0xffffff,
      align: "center",
      resolution: window.devicePixelRatio || 1,
    });
  }, [viewportConfig]);

  // ── Open animation (overlay fade → content fade) ─────────────────────────
  useEffect(() => {
    if (popupActive && !isClosing) {
      // If overlay is already fully visible (e.g. orientation change while open),
      // snap to target — don't replay fade-in from 0
      if (overlayAlpha >= OVERLAY_ALPHA) {
        if (containerRef.current) {
          containerRef.current.alpha = 1;
        }
        return;
      }

      const overlayAnim = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_MS,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      });

      let contentAnim = null;
      if (containerRef.current) {
        containerRef.current.alpha = 0;
        contentAnim = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          delay: 100,
          easing: "easeOutQuad",
        });
      }

      return () => {
        overlayAnim.pause();
        if (contentAnim) contentAnim.pause();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupActive, isClosing, showPopupFrame]);

  // ── Close animation (content fade → overlay fade → dispatch) ─────────────
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let contentAnim = null;

    if (containerRef.current) {
      contentAnim = anime({
        targets: containerRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    }

    overlayAnim = anime({
      targets: { alpha: OVERLAY_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_MS,
      delay: 100,
      easing: "easeInQuad",
      update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeBetSelectPopup());
        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (contentAnim) contentAnim.pause();
    };
  }, [isClosing, dispatch]);

  // Get button config for close button positioning - memoized
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isMobilePortraitFullscreen = useMemo(
    () =>
      !showPopupFrame &&
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT,
    [showPopupFrame, device, orientation],
  );

  const isMobileSmallPortraitFullscreen = useMemo(
    () => isMobileSmall && isMobilePortraitFullscreen,
    [isMobileSmall, isMobilePortraitFullscreen],
  );

  // Close button size multiplier — varies by mode/orientation
  const closeButtonSizeMultiplier = useMemo(() => {
    if (showPopupFrame) return 0.35;
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE
    )
      return 0.5;
    if (isMobileSmallPortraitFullscreen) return 0.4 * 1.12;
    return 0.4;
  }, [showPopupFrame, device, orientation, isMobileSmallPortraitFullscreen]);

  /**
   * Popup container position calculation
   */
  const popupContainerPosition = useMemo(() => {
    if (showPopupFrame && toolbar && toolbar.width && popupSize.width) {
      if (device === PixiSettings.DEVICE.DESKTOP) {
        // Desktop: exact mirror of AutoplayPopup but on the left side
        const toolbarX = (dimensions.width - toolbar.width) / 2;
        const betRefX = toolbarX + closeButtonConfig.width * 4.4;
        const toolbarTopY = dimensions.height - toolbar.height;
        const gap = toolbar.height * 0.25;
        return {
          x: betRefX + popupSize.width * 0.65,
          y: toolbarTopY - popupSize.height / 2 - gap,
        };
      }

      // Tablet landscape: anchor bottom edge above toolbar
      const config = POPUP_CONFIG[device]?.[orientation] || {
        positionOffsetLeft: 1.25,
        positionOffsetBottom: 2.25,
      };
      const toolbarLeftEdge = (dimensions.width - toolbar.width) / 2;
      const offsetFromLeft = popupSize.width * config.positionOffsetLeft;
      const toolbarTopY = dimensions.height - toolbar.height;
      const gap = toolbar.height * 0.25;
      return {
        x: toolbarLeftEdge + offsetFromLeft,
        y: toolbarTopY - popupSize.height / 2 - gap,
      };
    }
    // Fullscreen - centered
    return {
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    };
  }, [
    showPopupFrame,
    dimensions,
    toolbar,
    popupSize,
    closeButtonConfig,
    device,
    orientation,
  ]);

  // Close button position - aligned to padding edges (top-right)
  const closeButtonPosition = useMemo(() => {
    if (!viewportConfig) return { x: 0, y: 0 };

    // Close button has anchor 0.5, so offset by half its size to align with padding
    const buttonSize = closeButtonConfig.width * closeButtonSizeMultiplier;
    const relativeX =
      viewportConfig.maxWidth - buttonSize / 2 - (showPopupFrame ? 6 : 0);
    // Windowed: vertically centered with title; Fullscreen mobile portrait: sit in top padding
    const relativeY = showPopupFrame
      ? viewportConfig.titleFontSize / 2 - 4
      : isMobileSmallPortraitFullscreen
        ? buttonSize / 2 - buttonSize * 4
        : isMobilePortraitFullscreen
          ? buttonSize / 2 - buttonSize * 3.25
          : buttonSize / 2;

    return {
      x: popupContainerPosition.x + viewportConfig.x + relativeX,
      y: popupContainerPosition.y + viewportConfig.y + relativeY,
    };
  }, [
    viewportConfig,
    popupContainerPosition,
    closeButtonConfig,
    closeButtonSizeMultiplier,
    showPopupFrame,
    isMobilePortraitFullscreen,
    isMobileSmallPortraitFullscreen,
  ]);

  if (!popupFrameTexture || !viewportConfig || !titleStyle) {
    return null;
  }
  if (!popupActive && !isClosing) return null;

  return (
    <Container
      ref={containerRef}
      name="secondary_bet_select_overlay"
      zIndex={10}
      sortableChildren={true}
      eventMode="static"
    >
      {/* Overlay — catches clicks, blocks game underneath */}
      {showPopupFrame ? (
        <Graphics
          name="secondary_bet_select_click_outside"
          zIndex={0}
          draw={(g) => {
            g.clear();
            g.beginFill(0x000000, 0.001);
            // g.drawRect(0, 0, dimensions.width, dimensions.height);
            // g.drawRect(0, -(dimensions.uiBoxOffsetY || 0), dimensions.width, dimensions.height + 2 * (dimensions.uiBoxOffsetY || 0)); // the whole frame, also around the HUD block
            g.drawRect(-(dimensions.uiBoxOffsetX || 0), -(dimensions.uiBoxOffsetY || 0), dimensions.width + 2 * (dimensions.uiBoxOffsetX || 0), dimensions.height + 2 * (dimensions.uiBoxOffsetY || 0)); // ...and beside the upright 9:16 column (an iPad, 2026-10-29)
            g.endFill();
          }}
          alpha={overlayAlpha > 0 ? 1 : 0}
          eventMode="static"
          onpointertap={handleClose}
        />
      ) : (
        <Graphics
          name="secondary_bet_select_background"
          zIndex={0}
          draw={(g) => {
            g.clear();
            g.beginFill(0x161616, 1);
            // g.drawRect(0, 0, dimensions.width, dimensions.height);
            // g.drawRect(0, -(dimensions.uiBoxOffsetY || 0), dimensions.width, dimensions.height + 2 * (dimensions.uiBoxOffsetY || 0)); // the whole frame, also around the HUD block
            g.drawRect(-(dimensions.uiBoxOffsetX || 0), -(dimensions.uiBoxOffsetY || 0), dimensions.width + 2 * (dimensions.uiBoxOffsetX || 0), dimensions.height + 2 * (dimensions.uiBoxOffsetY || 0)); // ...and beside the upright 9:16 column (an iPad, 2026-10-29)
            g.endFill();
          }}
          alpha={overlayAlpha}
          // Fullscreen mode (mobile portrait/landscape, tablet portrait): the
          // popup may ONLY be closed via the X button — tapping the background
          // must not dismiss it. eventMode stays "static" so the fill still
          // absorbs taps (blocks the game underneath), it just no longer closes.
          eventMode="static"
        />
      )}

      {/* Popup Container */}
      <Container
        name="secondary_bet_select_popup"
        x={popupContainerPosition.x}
        y={popupContainerPosition.y}
        zIndex={1}
      >
        {/* Popup Frame Sprite (Tablet Landscape only) */}
        {showPopupFrame && (
          <Sprite
            texture={popupFrameTexture}
            anchor={0.5}
            width={popupSize.width}
            height={popupSize.height}
            roundPixels={true}
          />
        )}

        {/* Content Viewport Container */}
        <Container
          name="secondary_bet_select_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
          sortableChildren={true}
        >
          {/* Title — not interactive so it cannot steal taps from the X */}
          <Text
            text={titleText}
            style={titleStyle}
            anchor={{ x: 0, y: 0 }}
            roundPixels={true}
            eventMode="none"
            x={
              showPopupFrame
                ? 0
                : device === PixiSettings.DEVICE.MOBILE &&
                    orientation === PixiSettings.ORIENTATION.LANDSCAPE
                  ? -8
                  : 0
            }
            y={0}
          />

          {/* Scrollable Content */}
          <Container
            name="scrollable_content_container"
            y={viewportConfig.titleYOffset}
            zIndex={1}
          >
            <BetSelectContent
              bets={bets}
              selectedBet={selectedBet}
              balance={balance}
              currency={currency}
              onBetSelect={handleBetSelect}
              viewportConfig={viewportConfig}
              gridColumns={gridColumns}
              betButtonConfig={betButtonConfig}
              buttonSpacing={buttonSpacing}
              rowSpacing={rowSpacing}
              leftAlign={showPopupFrame}
            />
          </Container>

          {/* Close Button (Windowed mode — rendered last so zIndex wins hit tests) */}
          {showPopupFrame && (
            <Container
              name="secondary_bet_select_close_button_windowed"
              x={
                viewportConfig.maxWidth -
                (closeButtonConfig.width * closeButtonSizeMultiplier * 1.0) / 2
              }
              y={
                (closeButtonConfig.height * closeButtonSizeMultiplier * 1.0) / 2
              }
              zIndex={10}
            >
              <ButtonMenuClose
                onClose={handleClose}
                x={0}
                y={0}
                isSettings={true}
                sizeMultiplier={closeButtonSizeMultiplier * 1.0}
              />
            </Container>
          )}
        </Container>
      </Container>

      {/* Close Button - fullscreen only (windowed close is inside content viewport) */}
      {!showPopupFrame && (
        <Container
          name="secondary_bet_select_close_button_container"
          x={closeButtonPosition.x}
          y={closeButtonPosition.y}
          zIndex={2}
        >
          <ButtonMenuClose
            onClose={handleClose}
            x={0}
            y={0}
            isSettings={true}
            sizeMultiplier={closeButtonSizeMultiplier}
          />
        </Container>
      )}
    </Container>
  );
};

export default BetSelectPopup;
